import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Clock, Building2, Users, MapPin, Calendar, IndianRupee, Send, CheckCircle2, Timer } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

export default function VendorOpenRFPs() {
  const { user } = useAuth();
  const [openRFPs, setOpenRFPs] = useState([]);
  const [myPitches, setMyPitches] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedRFP, setSelectedRFP] = useState(null);
  const [showPitchDialog, setShowPitchDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alternateDatesDate, setAlternateDatesDate] = useState(null);

  useEffect(() => {
    fetchData();
    // Refresh every minute to update time remaining
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [rfpsRes, pitchesRes, propertiesRes] = await Promise.all([
        api.getBulkRFPs(),
        api.getVendorPitches(),
        api.getVendorProperties()
      ]);
      setOpenRFPs(rfpsRes.data);
      setMyPitches(pitchesRes.data);
      setProperties(propertiesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTimeRemaining = (deadline) => {
    const now = new Date();
    const end = new Date(deadline);
    const diff = end - now;
    
    if (diff <= 0) return { text: 'Expired', expired: true };
    
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return { text: `${minutes}m remaining`, expired: false, urgent: minutes < 15 };
    
    const hours = Math.floor(minutes / 60);
    return { text: `${hours}h ${minutes % 60}m remaining`, expired: false, urgent: false };
  };

  const hasPitched = (rfpId) => {
    return myPitches.some(p => p.bulk_rfp_id === rfpId);
  };

  const handleOpenPitchDialog = async (rfp) => {
    try {
      const res = await api.getBulkRFPDetail(rfp.id);
      setSelectedRFP(res.data);
      setShowPitchDialog(true);
    } catch (error) {
      toast.error('Failed to load RFP details');
    }
  };

  const handleSubmitPitch = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(e.target);
    
    try {
      await api.submitPitch(selectedRFP.id, {
        bulk_rfp_id: selectedRFP.id,
        property_id: formData.get('property_id'),
        accommodation_total: parseFloat(formData.get('accommodation_total')),
        venue_total: parseFloat(formData.get('venue_total')),
        fnb_total: parseFloat(formData.get('fnb_total')),
        additional_services_total: parseFloat(formData.get('additional_services_total')),
        taxes_percentage: parseFloat(formData.get('taxes_percentage')),
        total_quoted_amount: parseFloat(formData.get('total_quoted_amount')),
        rooms_available: parseInt(formData.get('rooms_available')),
        venue_available: formData.get('venue_available') === 'yes',
        dates_confirmed: formData.get('dates_confirmed') === 'yes',
        alternate_dates_available: formData.get('alternate_dates_available') || null,
        inclusions: formData.get('inclusions').split(',').map(s => s.trim()),
        exclusions: formData.get('exclusions').split(',').map(s => s.trim()),
        complimentary_offers: formData.get('complimentary_offers').split(',').map(s => s.trim()).filter(s => s),
        validity_days: parseInt(formData.get('validity_days')),
        payment_terms: formData.get('payment_terms'),
        cancellation_policy: formData.get('cancellation_policy'),
        special_notes: formData.get('special_notes') || null
      });
      toast.success('Pitch submitted successfully!');
      setShowPitchDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit pitch');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter out RFPs already pitched
  const availableRFPs = openRFPs.filter(rfp => !hasPitched(rfp.id));

  if (!user || user.role !== 'vendor') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Access Denied</h2>
          <p className="text-slate-800 mb-4">Only vendors can access this page</p>
          <Link to="/"><Button>Go Home</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/vendor">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </Link>
            <h1 className="luxury-text text-xl font-semibold text-midnight-navy">Open RFPs</h1>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Timer className="w-4 h-4 text-amber-500" />
            <span className="text-slate-800">Pitch within 1 hour to qualify</span>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-midnight-navy"></div>
          </div>
        ) : (
          <Tabs defaultValue="open">
            <TabsList>
              <TabsTrigger value="open">Open RFPs ({availableRFPs.length})</TabsTrigger>
              <TabsTrigger value="pitched">My Pitches ({myPitches.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="open" className="mt-6">
              {availableRFPs.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-lg border">
                  <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-800" />
                  <h2 className="text-xl font-semibold mb-2">No Open RFPs</h2>
                  <p className="text-slate-800">Check back soon for new bulk booking requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {availableRFPs.map((rfp) => {
                    const timeInfo = getTimeRemaining(rfp.pitch_deadline);
                    return (
                      <div key={rfp.id} className={`bg-white rounded-lg border p-6 ${timeInfo.expired ? 'opacity-50' : ''}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-xl font-semibold">{rfp.event_name}</h3>
                              {rfp.is_verified && (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs rounded-full">
                                  ✓ Verified
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-800 capitalize">
                              {rfp.event_type.replace('_', ' ')} • {rfp.company_name}
                            </p>
                          </div>
                          <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                            timeInfo.expired ? 'bg-red-100 text-red-800' :
                            timeInfo.urgent ? 'bg-amber-100 text-amber-800 animate-pulse' :
                            'bg-emerald-100 text-emerald-800'
                          }`}>
                            <Clock className="w-4 h-4" />
                            <span className="text-sm font-medium">{timeInfo.text}</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-800" />
                            <span className="text-sm">{rfp.min_attendees}-{rfp.max_attendees} guests</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-800" />
                            <span className="text-sm">{rfp.preferred_cities?.slice(0, 2).join(', ')}{rfp.preferred_cities?.length > 2 ? '...' : ''}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-800" />
                            <span className="text-sm">{rfp.event_duration_days} days</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-800" />
                            <span className="text-sm">{rfp.total_rooms_required} rooms</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <IndianRupee className="w-4 h-4 text-slate-800" />
                            <span className="text-sm font-medium text-emerald-700">
                              ₹{(rfp.total_budget_min / 100000).toFixed(1)}L - {(rfp.total_budget_max / 100000).toFixed(1)}L
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t">
                          <p className="text-sm text-slate-800">
                            {rfp.pitches_received} pitches received
                          </p>
                          <Button 
                            onClick={() => handleOpenPitchDialog(rfp)} 
                            disabled={timeInfo.expired || properties.length === 0}
                            className="bg-midnight-navy"
                          >
                            <Send className="w-4 h-4 mr-2" /> Submit Pitch
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="pitched" className="mt-6">
              {myPitches.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border">
                  <p className="text-slate-800">You haven't submitted any pitches yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {myPitches.map((pitch) => (
                    <div key={pitch.id} className="bg-white rounded-lg border p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">{pitch.property_name}</h3>
                          <p className="text-sm text-slate-800">{pitch.property_city}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-midnight-navy">
                            ₹{pitch.total_quoted_amount.toLocaleString()}
                          </p>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            pitch.status === 'selected' ? 'bg-emerald-100 text-emerald-800' :
                            pitch.status === 'shortlisted' ? 'bg-blue-100 text-blue-800' :
                            pitch.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-slate-100 text-slate-800'
                          }`}>
                            {pitch.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-slate-800">
                        Submitted: {new Date(pitch.submitted_at).toLocaleString()}
                        {pitch.is_within_deadline && (
                          <span className="ml-2 text-emerald-600">✓ On-time</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Pitch Dialog */}
      <Dialog open={showPitchDialog} onOpenChange={setShowPitchDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submit Pitch for {selectedRFP?.event_name}</DialogTitle>
          </DialogHeader>
          
          {selectedRFP && (
            <div className="mb-4 p-4 bg-slate-50 rounded-lg text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><strong>Event:</strong> {selectedRFP.event_type}</div>
                <div><strong>Attendees:</strong> {selectedRFP.min_attendees}-{selectedRFP.max_attendees}</div>
                <div><strong>Rooms:</strong> {selectedRFP.total_rooms_required}</div>
                <div><strong>Duration:</strong> {selectedRFP.event_duration_days} days</div>
                <div className="col-span-2"><strong>Budget:</strong> ₹{selectedRFP.total_budget_min?.toLocaleString()} - ₹{selectedRFP.total_budget_max?.toLocaleString()}</div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmitPitch} className="space-y-6">
            {/* Property Selection */}
            <div>
              <Label>Select Property *</Label>
              <Select name="property_id" required>
                <SelectTrigger><SelectValue placeholder="Choose a property" /></SelectTrigger>
                <SelectContent>
                  {properties.map(prop => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.name} - {prop.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Pricing */}
            <div className="border rounded-lg p-4">
              <h3 className="font-medium mb-4">Pricing Breakdown</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Accommodation Total (₹) *</Label>
                  <Input type="number" name="accommodation_total" min="0" required />
                </div>
                <div>
                  <Label>Venue Total (₹) *</Label>
                  <Input type="number" name="venue_total" min="0" required />
                </div>
                <div>
                  <Label>F&B Total (₹) *</Label>
                  <Input type="number" name="fnb_total" min="0" required />
                </div>
                <div>
                  <Label>Additional Services (₹)</Label>
                  <Input type="number" name="additional_services_total" min="0" defaultValue="0" />
                </div>
                <div>
                  <Label>Taxes (%)</Label>
                  <Input type="number" name="taxes_percentage" min="0" max="50" step="0.1" defaultValue="18" />
                </div>
                <div>
                  <Label>Total Quoted Amount (₹) *</Label>
                  <Input type="number" name="total_quoted_amount" min="0" required />
                </div>
              </div>
            </div>

            {/* Availability */}
            <div className="border rounded-lg p-4">
              <h3 className="font-medium mb-4">Availability</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Rooms Available *</Label>
                  <Input type="number" name="rooms_available" min="0" required />
                </div>
                <div>
                  <Label>Venue Available</Label>
                  <Select name="venue_available" defaultValue="yes">
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Dates Confirmed</Label>
                  <Select name="dates_confirmed" defaultValue="yes">
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4">
                <Label>Alternate Dates Available</Label>
                <DatePicker
                  selected={alternateDatesDate}
                  onChange={(date) => setAlternateDatesDate(date)}
                  minDate={new Date()}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                  calendarClassName="custom-calendar"
                  wrapperClassName="w-full"
                />
                <input type="hidden" name="alternate_dates_available" value={alternateDatesDate ? alternateDatesDate.toISOString().split('T')[0] : ''} />
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Inclusions (comma-separated) *</Label>
                <Textarea 
                  name="inclusions" 
                  placeholder="Welcome drinks, WiFi, Parking, Breakfast..." 
                  required 
                />
              </div>
              <div>
                <Label>Exclusions (comma-separated) *</Label>
                <Textarea 
                  name="exclusions" 
                  placeholder="Alcohol, Personal expenses, Extra meals..." 
                  required 
                />
              </div>
            </div>

            {/* Complimentary Offers */}
            <div>
              <Label>Complimentary Offers (comma-separated)</Label>
              <Input name="complimentary_offers" placeholder="Spa credits, Room upgrade, Airport pickup..." />
            </div>

            {/* Terms */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Quote Validity (days)</Label>
                <Input type="number" name="validity_days" min="1" max="30" defaultValue="7" />
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select name="payment_terms" defaultValue="50_advance">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="100_advance">100% Advance</SelectItem>
                    <SelectItem value="50_advance">50% Advance</SelectItem>
                    <SelectItem value="30_advance">30% Advance</SelectItem>
                    <SelectItem value="post_event">Post Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cancellation Policy</Label>
                <Select name="cancellation_policy" defaultValue="standard">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flexible">Flexible</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="strict">Strict</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Special Notes */}
            <div>
              <Label>Special Notes</Label>
              <Textarea name="special_notes" placeholder="Any additional information or special offers..." />
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowPitchDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1 bg-midnight-navy">
                {isSubmitting ? 'Submitting...' : 'Submit Pitch'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
